import React, { forwardRef, useId } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    helperText?: string;
    icon?: React.ReactNode;
    showInfoIcon?: boolean;
}

/**
 * Reusable Input component matching the onboarding design mockups
 * Features: borderless design, light gray background, focus ring, error states
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, error, helperText, icon, showInfoIcon, className = '', id, ...props }, ref) => {
        const generatedId = useId();
        const inputId = id || generatedId;

        return (
            <div className="w-full">
                {label && (
                    <div className="flex items-center gap-1 mb-1">
                        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
                            {label}
                        </label>
                        {showInfoIcon && (
                            <div className="group relative">
                                <svg
                                    className="w-4 h-4 text-gray-400 cursor-help"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                        )}
                    </div>
                )}
                <div className="relative">
                    {icon && (
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            {icon}
                        </div>
                    )}
                    <input
                        ref={ref}
                        id={inputId}
                        className={`
              w-full px-4 py-3 
              bg-gray-100 
              text-gray-900 text-base
              placeholder-gray-400
              rounded-xl
              border-0
              focus:outline-none focus:ring-2 focus:ring-[#00A8E8]
              transition-all duration-200
              ${error ? 'ring-2 ring-red-500' : ''}
              ${icon ? 'pl-10' : ''}
              ${className}
            `}
                        {...props}
                    />
                </div>
                {error && (
                    <p className="mt-1 text-sm text-red-500">{error}</p>
                )}
                {helperText && !error && (
                    <p className="mt-1 text-sm text-gray-500">{helperText}</p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;
